import { PackageStatus } from '@allocab/allocab-dedicated-driver-types/lib/src/dedicated-package/types';
import { RouteSolid } from '@allocab/allocab-icons';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { twJoin } from 'tailwind-merge';

import { CheckBox } from '../../components/atomic/CheckBox';
import { PrimaryButton } from '../../components/button/PrimaryButton';
import { SecondaryButton } from '../../components/button/SecondaryButton';
import { FilterBadge } from '../../components/FilterBadge';
import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { t } from '../../services/translation/translation';
import { getTripPackageNameStatus } from '../../utils/package';

export const STATUS_SEARCH_PARAMS_KEY = 'status';
const packageStatus: PackageStatus[] = ['DRAFT', 'PROPOSED', 'ACCEPTED', 'IN_PROGRESS', 'TERMINATED', 'CANCELLED'];

type Props = {
  onChange: (_key: string, _value: string | undefined) => void;
};

export const TripPackageStatusFilter = ({ onChange }: Props) => {
  const [searchParams] = useSearchParams();
  const [isVisible, setIsVisible] = useState(false);
  const status = searchParams.get(STATUS_SEARCH_PARAMS_KEY);
  const statusFromParams = status?.split(',') as PackageStatus[] | undefined;
  const [selectedStates, setSelectedStates] = useState<PackageStatus[]>(statusFromParams ?? packageStatus);

  const ref = useOnClickOutside(
    useCallback(() => {
      setIsVisible(false);
    }, []),
  );

  const handleSave = () => {
    if (selectedStates.length < packageStatus.length) {
      onChange(STATUS_SEARCH_PARAMS_KEY, selectedStates.join(','));
    } else {
      onChange(STATUS_SEARCH_PARAMS_KEY, undefined);
    }
    setIsVisible(false);
  };

  const handleReset = () => {
    setSelectedStates(packageStatus);
    onChange(STATUS_SEARCH_PARAMS_KEY, undefined);
    setIsVisible(false);
  };

  return (
    <div className="relative flex items-center justify-center">
      <FilterBadge onClick={() => setIsVisible(v => !v)} isActive={!!statusFromParams}>
        <RouteSolid width={12} height={12} className="fill-current" />
        <span>
          {statusFromParams ? statusFromParams.map(s => getTripPackageNameStatus(s)).join(',') : t('common.status')}
        </span>
      </FilterBadge>
      <div
        ref={ref}
        className={twJoin(
          'absolute top-10 z-10 w-48 bg-white rounded-lg shadow dark:bg-gray-700',
          !isVisible && 'hidden',
        )}
      >
        <ul className="p-3 space-y-1 text-sm text-gray-700 dark:text-gray-200">
          {packageStatus.map(status => (
            <li key={status}>
              <CheckBox
                id={`status-filter-${status}`}
                checked={selectedStates.includes(status)}
                onChange={value =>
                  value
                    ? setSelectedStates(p => [...p, status])
                    : setSelectedStates(p => (p.length === 1 ? p : p.filter(s => s !== status)))
                }
                label={getTripPackageNameStatus(status)}
              />
            </li>
          ))}
        </ul>
        <div className="space-y-2 px-2 mb-2">
          <PrimaryButton className="w-full" onClick={handleSave}>
            {t('common.apply')}
          </PrimaryButton>

          <SecondaryButton className="w-full" onClick={handleReset}>
            {t('common.reset')}
          </SecondaryButton>
        </div>
      </div>
    </div>
  );
};
